import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props { children: ReactNode }
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] caught", error, info);
    // Best-effort server-side log so blank-page regressions show up in /admin/logs.
    try {
      void fetch("/api/client-error", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: error.message,
          stack: (error.stack ?? "").slice(0, 2000),
          componentStack: (info.componentStack ?? "").slice(0, 2000),
          url: window.location.href,
          ua: navigator.userAgent.slice(0, 200),
        }),
      });
    } catch { /* ignore */ }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="container" style={{ padding: "3rem 0", maxWidth: 640 }}>
        <h2>Something broke 💥</h2>
        <p style={{ color: "var(--fg-muted)", marginTop: "1rem" }}>
          The page hit an unexpected error and couldn't render. The team has been notified.
        </p>
        <details style={{ marginTop: "1.25rem", color: "var(--fg-subtle)", fontSize: "0.85rem" }}>
          <summary style={{ cursor: "pointer" }}>Show error</summary>
          <pre style={{
            background: "var(--bg-elevated)", padding: "1rem", borderRadius: "var(--radius)",
            overflow: "auto", fontSize: "0.78rem", marginTop: "0.5rem",
            whiteSpace: "pre-wrap", wordBreak: "break-word",
          }}>
            {this.state.error.message}{"\n\n"}{this.state.error.stack ?? ""}
          </pre>
        </details>
        <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button onClick={this.reset} className="primary">Try again</button>
          <a href="/" style={{ borderBottom: "1px solid var(--border)", padding: "0.65rem 1rem", borderRadius: "var(--radius)", textDecoration: "none" }}>Go home</a>
        </div>
      </div>
    );
  }
}
